<template>
  <div v-loading="loading" class="dashboard">
    <!-- 顶部统计卡片 -->
    <div class="dashboard__stats">
      <div v-for="group in statGroups" :key="group.title" class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title">{{ group.title }}</span>
        </div>
        <div class="dash-card__body">
          <div class="stat-grid">
            <div v-for="item in group.items" :key="item.label">
              <div class="stat-item__label">{{ item.label }}</div>
              <div class="stat-item__value" :style="{ color: item.color }" :title="item.tip">{{ item.value }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 下方图表 -->
    <div class="dashboard__charts">
      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title">用户趋势图（近30天）</span>
        </div>
        <div class="dash-card__body">
          <div ref="userTrendChart" class="chart"></div>
        </div>
      </div>
      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title">资金趋势图（近30天）</span>
        </div>
        <div class="dash-card__body">
          <div ref="fundTrendChart" class="chart"></div>
        </div>
      </div>
      <div class="dash-card">
        <div class="dash-card__header">
          <span class="dash-card__title">24小时交易量</span>
        </div>
        <div class="dash-card__body">
          <div ref="hourlyVolumeChart" class="chart"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts/core'
import { BarChart, LineChart } from 'echarts/charts'
import { GridComponent, LegendComponent, TooltipComponent } from 'echarts/components'
import { CanvasRenderer } from 'echarts/renderers'
import { dashboardOverview } from '@/api/dashboard'

echarts.use([LineChart, BarChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer])

const COLOR_TEXT = '#111827'
const COLOR_MUTED = '#9ca3af'
const COLOR_BORDER = '#e8ecf2'
const COLOR_ACCENT = '#00897b'
const COLOR_SUCCESS = '#16a34a'
const COLOR_WARNING = '#e6a817'
const COLOR_DANGER = '#dc2626'
const COLOR_WITHDRAW = '#94a3b8'

const EMPTY = '--'

export default {
  name: 'Dashboard',
  data() {
    return {
      loading: false,
      overview: null
    }
  },
  computed: {
    user() {
      return (this.overview && this.overview.user) || {}
    },
    trade() {
      return (this.overview && this.overview.trade) || {}
    },
    finance() {
      return (this.overview && this.overview.finance) || {}
    },
    statGroups() {
      return [
        {
          title: '用户概况',
          items: [
            { label: '总用户数', value: this.formatInt(this.user.totalUsers), color: COLOR_TEXT },
            { label: '今日新增', value: this.formatSignedInt(this.user.todayNew), color: COLOR_SUCCESS },
            { label: '活跃用户', value: this.formatInt(this.user.activeUsers), color: COLOR_ACCENT },
            { label: '待审核KYC', value: this.formatInt(this.user.pendingKyc), color: COLOR_WARNING }
          ]
        },
        {
          title: '交易概况',
          items: [
            { label: '今日交易笔数', value: this.formatInt(this.trade.todayTradeCount), color: COLOR_TEXT },
            { label: '持仓中', value: this.formatInt(this.trade.openPositionCount), color: COLOR_ACCENT },
            { label: '挂单中', value: this.formatInt(this.trade.pendingOrderCount), color: COLOR_WARNING },
            { label: '交易成功率', value: this.formatRate(this.trade.successRate), color: COLOR_SUCCESS }
          ]
        },
        {
          title: '财务概况',
          items: [
            { label: '今日入金（USD）', value: this.formatMoney(this.finance.todayDeposit), tip: this.moneyTip(this.finance.todayDeposit), color: COLOR_SUCCESS },
            { label: '今日出金（USD）', value: this.formatMoney(this.finance.todayWithdraw), tip: this.moneyTip(this.finance.todayWithdraw), color: COLOR_DANGER },
            { label: '净入金（USD）', value: this.formatSignedMoney(this.finance.netDeposit), tip: this.moneyTip(this.finance.netDeposit), color: COLOR_ACCENT },
            { label: '总余额（USD）', value: this.formatMoney(this.finance.totalBalance), tip: this.moneyTip(this.finance.totalBalance), color: COLOR_TEXT }
          ]
        }
      ]
    }
  },
  created() {
    // 图表实例与定时器不参与响应式，避免 Vue 深度代理 echarts 对象
    this.charts = {}
    this.resizeTimer = null
  },
  mounted() {
    this.initCharts()
    window.addEventListener('resize', this.handleResize)
    this.loadData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    if (this.resizeTimer) {
      clearTimeout(this.resizeTimer)
    }
    Object.keys(this.charts).forEach(key => {
      if (this.charts[key]) {
        this.charts[key].dispose()
        this.charts[key] = null
      }
    })
  },
  methods: {
    /** 一次请求拉取整个看板（后端已并行聚合 + 缓存60秒） */
    loadData() {
      this.loading = true
      dashboardOverview()
        .then(res => {
          this.overview = res.result || null
          this.renderCharts()
        })
        .finally(() => {
          this.loading = false
        })
    },

    /** 图表实例集中管理，避免重复 init 泄漏 */
    initCharts() {
      this.charts = {
        userTrend: echarts.init(this.$refs.userTrendChart),
        fundTrend: echarts.init(this.$refs.fundTrendChart),
        hourlyVolume: echarts.init(this.$refs.hourlyVolumeChart)
      }
    },

    renderCharts() {
      const userTrend = (this.overview && this.overview.userTrend) || []
      const fundTrend = (this.overview && this.overview.fundTrend) || []
      const hourlyVolume = (this.overview && this.overview.hourlyVolume) || []

      this.charts.userTrend.setOption(this.buildUserTrendOption(userTrend))
      this.charts.fundTrend.setOption(this.buildFundTrendOption(fundTrend))
      this.charts.hourlyVolume.setOption(this.buildHourlyVolumeOption(hourlyVolume))
    },

    handleResize() {
      if (this.resizeTimer) {
        clearTimeout(this.resizeTimer)
      }
      this.resizeTimer = setTimeout(() => {
        Object.keys(this.charts || {}).forEach(key => {
          if (this.charts[key]) {
            this.charts[key].resize()
          }
        })
      }, 200)
    },

    buildUserTrendOption(rows) {
      return {
        grid: { left: 4, right: 8, top: 34, bottom: 0, containLabel: true },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross', label: { backgroundColor: '#6b7280' } }
        },
        legend: {
          right: 0,
          top: 0,
          itemWidth: 10,
          itemHeight: 8,
          textStyle: { fontSize: 11, color: COLOR_MUTED },
          data: ['新增', '活跃']
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: rows.map(r => r.label),
          axisLabel: { fontSize: 10, color: COLOR_MUTED, hideOverlap: true },
          axisLine: { lineStyle: { color: COLOR_BORDER } },
          axisTick: { show: false }
        },
        yAxis: this.buildValueAxis(),
        series: [
          {
            name: '新增',
            type: 'line',
            smooth: true,
            symbol: 'none',
            data: rows.map(r => r.newUsers || 0),
            lineStyle: { width: 2, color: COLOR_ACCENT },
            itemStyle: { color: COLOR_ACCENT },
            areaStyle: { color: 'rgba(0,137,123,0.10)' }
          },
          {
            name: '活跃',
            type: 'line',
            smooth: true,
            symbol: 'none',
            data: rows.map(r => r.activeUsers || 0),
            lineStyle: { width: 1.5, type: 'dashed', color: COLOR_WITHDRAW },
            itemStyle: { color: COLOR_WITHDRAW }
          }
        ]
      }
    },

    buildFundTrendOption(rows) {
      return {
        grid: { left: 4, right: 8, top: 34, bottom: 0, containLabel: true },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'cross', label: { backgroundColor: '#6b7280' } },
          valueFormatter: value => this.moneyTip(value)
        },
        legend: {
          right: 0,
          top: 0,
          itemWidth: 10,
          itemHeight: 8,
          textStyle: { fontSize: 11, color: COLOR_MUTED },
          data: ['入金', '出金']
        },
        xAxis: {
          type: 'category',
          boundaryGap: false,
          data: rows.map(r => r.label),
          axisLabel: { fontSize: 10, color: COLOR_MUTED, hideOverlap: true },
          axisLine: { lineStyle: { color: COLOR_BORDER } },
          axisTick: { show: false }
        },
        yAxis: this.buildValueAxis(),
        series: [
          {
            name: '入金',
            type: 'line',
            smooth: true,
            symbol: 'none',
            data: rows.map(r => r.deposit || 0),
            lineStyle: { width: 2, color: COLOR_SUCCESS },
            itemStyle: { color: COLOR_SUCCESS },
            areaStyle: { color: 'rgba(22,163,74,0.10)' }
          },
          {
            name: '出金',
            type: 'line',
            smooth: true,
            symbol: 'none',
            data: rows.map(r => r.withdraw || 0),
            lineStyle: { width: 1.5, type: 'dashed', color: COLOR_WITHDRAW },
            itemStyle: { color: COLOR_WITHDRAW }
          }
        ]
      }
    },

    buildHourlyVolumeOption(rows) {
      return {
        grid: { left: 4, right: 8, top: 20, bottom: 0, containLabel: true },
        tooltip: {
          trigger: 'axis',
          axisPointer: { type: 'shadow' }
        },
        xAxis: {
          type: 'category',
          data: rows.map(r => r.label),
          axisLabel: { fontSize: 10, color: COLOR_MUTED, interval: 3, hideOverlap: true },
          axisLine: { lineStyle: { color: COLOR_BORDER } },
          axisTick: { show: false }
        },
        yAxis: this.buildValueAxis(),
        series: [
          {
            name: '交易量',
            type: 'bar',
            barMaxWidth: 12,
            data: rows.map(r => r.count || 0),
            itemStyle: { color: COLOR_ACCENT, borderRadius: [2, 2, 0, 0] }
          }
        ]
      }
    },

    buildValueAxis() {
      return {
        type: 'value',
        splitLine: { lineStyle: { color: '#f1f5f9' } },
        axisLabel: { fontSize: 10, color: COLOR_MUTED, formatter: value => this.compactNumber(value) },
        axisLine: { show: false },
        axisTick: { show: false }
      }
    },

    /** 整数展示，null 归 0，千分位 */
    formatInt(value) {
      if (value === null || value === undefined) {
        return '0'
      }
      return Number(value).toLocaleString('en-US')
    },

    /** 带正负号的整数（今日新增） */
    formatSignedInt(value) {
      const num = Number(value || 0)
      return (num > 0 ? '+' : '') + num.toLocaleString('en-US')
    },

    /** 成功率，无成交样本时后端返回 null */
    formatRate(value) {
      return value === null || value === undefined ? EMPTY : `${value}%`
    },

    /** 金额紧凑展示：$284K / $48.2M / $1.2B */
    formatMoney(value) {
      if (value === null || value === undefined) {
        return EMPTY
      }
      return '$' + this.compactNumber(value)
    },

    /** 带正负号的金额（净入金） */
    formatSignedMoney(value) {
      if (value === null || value === undefined) {
        return EMPTY
      }
      const num = Number(value)
      return (num > 0 ? '+' : num < 0 ? '-' : '') + '$' + this.compactNumber(Math.abs(num))
    },

    /** 金额精确值，用于 hover 提示 */
    moneyTip(value) {
      if (value === null || value === undefined) {
        return EMPTY
      }
      return '$' + Number(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },

    compactNumber(value) {
      const num = Number(value || 0)
      const abs = Math.abs(num)
      if (abs >= 1e9) {
        return (num / 1e9).toFixed(1) + 'B'
      }
      if (abs >= 1e6) {
        return (num / 1e6).toFixed(1) + 'M'
      }
      if (abs >= 1e3) {
        return Math.round(num / 1e3) + 'K'
      }
      return Number.isInteger(num) ? String(num) : num.toFixed(2)
    }
  }
}
</script>

<style lang="scss" scoped>
.dashboard {
  padding: 24px;

  &__stats {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    margin-bottom: 20px;
  }

  &__charts {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
  }
}

.dash-card {
  background: #fff;
  border: 1px solid #e8ecf2;
  border-radius: 4px;

  &__header {
    padding: 12px 16px;
    border-bottom: 1px solid #e8ecf2;
  }

  &__title {
    font-size: 14px;
    font-weight: 600;
  }

  &__body {
    padding: 16px;
  }
}

.stat-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.stat-item {
  &__label {
    font-size: 12px;
    color: #9ca3af;
  }

  &__value {
    font-size: 24px;
    font-weight: 700;
    line-height: 1.35;
  }
}

.chart {
  height: 220px;
}
</style>
